import useManageParticipants from "../../hooks/useManageParticipants";
import CreateSharingInvitationModal from "../CreateSharingInvitationModal";
import { PublicUserOrganization } from "../../__generated__/graphql";
import Modal from "../Modal";
import { useEffect, useState } from "react";
import Button from "../Button";
import UserOrganizationItem from "../UserOrganizationItem";

interface ManageParticipantsModalProps {
  description: string;
  participantsFor: "event" | "event_document" | "project" | "project_document";
  eventId?: string;
  documentId?: string;
  projectId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedUserOrganizationIds: PublicUserOrganization[]) => void;
}

export default function ManageParticipantsModal({
  description,
  isOpen,
  onClose,
  participantsFor,
  eventId,
  documentId,
  projectId,
  onSubmit,
}: ManageParticipantsModalProps) {
  const {
    loading,
    error,
    possibleParticipants,
    alreadyAddedParticipants,
    organizationMembers,
  } = useManageParticipants({
    participantsFor,
    eventId,
    documentId,
    projectId,
  });

  const [selectedParticipants, setSelectedParticipants] = useState<
    PublicUserOrganization[]
  >(alreadyAddedParticipants);

  const handleSubmit = () => {
    onSubmit(selectedParticipants);
    onClose();
  };

  useEffect(() => {
    setSelectedParticipants(alreadyAddedParticipants);
  }, [isOpen, alreadyAddedParticipants]);

  if (loading) {
    return null;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (possibleParticipants.length === 0) {
    // Display invitation modal
    return (
      <CreateSharingInvitationModal
        title="Inviter des participants"
        description="Pour assigner des collaborateurs, vous devez d'abord les inviter à rejoindre Kontrol."
        isModalOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const onCollaboratorClick = (participant: PublicUserOrganization) => {
    if (
      selectedParticipants.some(
        (selectedParticipant) => selectedParticipant.id === participant.id
      )
    ) {
      setSelectedParticipants(
        selectedParticipants.filter(
          (selectedParticipant) => selectedParticipant.id !== participant.id
        )
      );
    } else {
      setSelectedParticipants([...selectedParticipants, participant]);
    }
  };

  const collaboratorComponent = (participant: PublicUserOrganization) => {
    return (
      <UserOrganizationItem
        key={participant.id}
        userOrganization={participant}
        selected={selectedParticipants.some(
          (selectedParticipant) => selectedParticipant.id === participant.id
        )}
        onClick={onCollaboratorClick}
      />
    );
  };

  const externalParticipantsList = () => {
    return (
      <div className="grid grid-cols-2 gap-2">
        {possibleParticipants.map((participant) =>
          collaboratorComponent(participant)
        )}
      </div>
    );
  };

  const organizationMembersList = () => {
    return (
      <div className="grid grid-cols-2 gap-2">
        {organizationMembers.map((member) => collaboratorComponent(member))}
      </div>
    );
  };

  return (
    <Modal
      className="w-xl overflow-y-auto custom-scrollbar"
      isModalOpen={isOpen}
      onClose={onClose}
      title="Gérer les participants"
      description={description}
    >
      {externalParticipantsList()}

      <div className="text-sm text-secondary">De votre organisation:</div>

      {organizationMembersList()}

      <div className="flex justify-end items-center gap-2">
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button onClick={handleSubmit}>Continuer</Button>
      </div>
    </Modal>
  );
}
